import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { getApartmentCoverPhotoUrl } from 'common/apartmentHelpers';

import { dataTable, actionDataTableLoadNextPage } from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

import { abbreviateNumber } from 'common/formatters';

import {
  DATA_TABLE_SOURCE_PARTNER_APARTMENTS,
  DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_ALL,
  DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_APP_IN_PROGRESS,
} from 'common/dataTableConstants';
import branch from 'client/core/branch';

import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
} from 'client/components/SrPartnerListingCard';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrText from 'client/components/SrText';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';

function makeStatLabel(title, total) {
  return `${abbreviateNumber(total, 1)} ${pluralize(title, total)}`;
}

function getDataTableSettingsFromProps(props) {
  const rootViewId = props.partnerApartmentsViewId || DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_ALL;
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_APARTMENTS,
    viewId: `${rootViewId}_${props.partner.id}`,
    filter: {
      'apartments.partnerId': props.partner.id,
    },
    customParams: {
      partnerApartments: {
        onlyActiveListings: true,
        sinceDate: props.sinceDate,
      },
    },
    sort: {
      activeListings: 'desc',
    },
    count: 40,
  };

  if (props.sinceDate) {
    dataTableSettings.viewId = `${dataTableSettings.viewId}_sinceDate:${props.sinceDate}`;
  }

  if (rootViewId === DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_APP_IN_PROGRESS) {
    dataTableSettings.customParams.partnerApartments.appInProgress = true;
  }

  return dataTableSettings;
}

const filterOptions = [
  {
    label: 'All',
    value: DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_ALL,
  },
  {
    label: 'Apps In Progress',
    value: DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_APP_IN_PROGRESS,
  },
];

@branch({
  partnerApartmentsViewId: ['partners', 'apartmentsViewId'],
})
@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
class SrDataTablePartnerApartments extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    items: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    partnerApartmentsViewId: PropTypes.string,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  UNSAFE_componentWillMount() {
    const { partnerApartmentsViewId } = this.props;
    if (!partnerApartmentsViewId) {
      this._onFilterClick(DATA_TABLE_VIEW_ID_PARTNER_APARTMENTS_ALL);
    }
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettingsFromProps(this.props));
    }
  };

  _onFilterClick = (viewId) => {
    const { dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['partners', 'apartmentsViewId'], viewId);
      state.commit();
    });
  };

  render() {
    const {
      items: apartments,
      partnerApartmentsViewId,
      onInfiniteScrollWrapperRef,
      dataTableApiState,
      dataTableIsFullyLoaded,
      partner,
    } = this.props;

    if (dataTableIsFullyLoaded && (!apartments || apartments.length === 0)) {
      return null;
    }

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrPartnerSection
          title="Active Apartments"
          headerRow={
            <div className="partnerDataTableFilterContainer flex" style={{ lineHeight: '40px' }}>
              {filterOptions.map(({ label, value }) => {
                const isActive = value === partnerApartmentsViewId;
                return (
                  <div key={value} className="partnerDataTableFilterOptionWrapper">
                    <SrText
                      size="large"
                      weight="medium"
                      color="blueGreycliff"
                      cursor={isActive ? null : 'pointer'}
                      decoration={isActive ? 'underline-yellow' : null}
                      font="greycliff"
                      opacity={isActive ? null : 0.5}
                      onClick={isActive ? null : () => this._onFilterClick(value)}
                      className="partnerDataTableFilterOption"
                    >
                      {label}
                    </SrText>
                  </div>
                );
              })}
            </div>
          }
        >
          <SrPartnerList singleRow>
            {dataTableIsFullyLoaded && apartments.length === 0 && (
              <SrDataRetrievalText text="No active apartments right now." />
            )}
            {apartments.map((apartment) => {
              return (
                <SrPartnerListingCard
                  key={apartment.id}
                  to={`/partner/${partner.slug}/apartment/${apartment.id}`}
                  photos={[getApartmentCoverPhotoUrl(apartment)]}
                  title={apartment.name}
                  caption={apartment.apartmentBuildings
                    .map(({ addressStreetDisplay }) => addressStreetDisplay)
                    .join(' & ')}
                  stats={[
                    makeStatLabel(
                      'Views',
                      apartment.rentlinxViews + apartment.srViews + apartment.supraShowings,
                    ), // eslint-disable-line max-len
                    makeStatLabel(
                      'Leads',
                      apartment.rentlinxLeads +
                        apartment.supraShowings +
                        apartment.srLeads +
                        apartment.locatorTourLeads +
                        apartment.partnerTourRequests,
                    ), // eslint-disable-line max-len
                    makeStatLabel('Tours', apartment.supraShowings + apartment.toursCompleted),
                  ]}
                  tags={
                    !apartment.appInProgress
                      ? null
                      : [
                          {
                            theme: SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
                            label: 'apps in progress',
                          },
                        ]
                  }
                />
              );
            })}
          </SrPartnerList>

          {dataTableApiState.inProgress && <div className="loading-ellipses" />}
        </SrPartnerSection>
      </div>
    );
  }
}

SrDataTablePartnerApartments.displayName = 'SrDataTablePartnerApartments';

export default SrDataTablePartnerApartments;
